import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URL, getFullImageUrl } from "../../api";
import Lottie from "lottie-react";
import liveChatbotAnimation from "../../assets/Live chatbot.json";

// ── Haversine distance (km) ──────────────────────────────────────────
function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Advanced NLP & Typo Correction ──────────────────────────────────────────
function levenshtein(a, b) {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1);
      }
    }
  }
  return matrix[b.length][a.length];
}

const DICTIONARY = {
  create: ["cretae", "creat", "make", "add", "new"],
  hotel: ["hotle", "htl", "lodge", "resort", "stay"],
  booking: ["bokng", "book", "reservation", "reserve"],
  host: ["hst", "vendor", "owner"],
  user: ["usr", "customer"],
  dashboard: ["dashbord", "board", "home"],
  employee: ["emplyee", "staff", "worker"],
  management: ["mangment", "manage"],
  billing: ["billng", "invoice", "payment", "price", "cost"],
  restaurant: ["restarant", "food", "cafe", "eat"],
  hospital: ["hosptal", "clinic", "medical"],
  salon: ["saloon", "spa", "haircut"],
  subscription: ["subcripton", "plan", "upgrade"]
};

function normalizeText(text) {
  const words = text.toLowerCase().replace(/[^\w\s]/g, "").split(/\s+/);
  return words.map(word => {
    let bestMatch = word;
    let lowestDistance = 3; // Typo threshold
    for (const [target, synonyms] of Object.entries(DICTIONARY)) {
      const candidates = [target, ...synonyms];
      for (const cand of candidates) {
        const dist = levenshtein(word, cand);
        // Dynamic threshold based on word length to prevent aggressive false positives
        const maxAllowedDist = word.length <= 4 ? 1 : 2;
        if (dist < lowestDistance && dist <= maxAllowedDist) {
          lowestDistance = dist;
          bestMatch = target;
        }
      }
    }
    return bestMatch;
  }).join(" ");
}

// ── Strict Security Policy ─────────────────────────────────────────────
const RESTRICTED_TERMS = [
  "password", "api key", "secret", "jwt", "token", "env", "private key",
  "database", "credentials", "ignore instruction", "developer mode",
  "show hidden", "system prompt", "act as root", "bypass", "config"
];

function detectSecurityThreat(text) {
  const lower = text.toLowerCase();
  return RESTRICTED_TERMS.some(term => lower.includes(term));
}

// ── Intent detection ─────────────────────────────────────────────────
const INTENTS = {
  nearby: /nearby|near me|close to me|around me|current location|where i am/i,
  hotel: /hotel|resort|stay|lodge|accommodation|room/i,
  hospital: /hospital|clinic|medical|doctor|health|emergency/i,
  salon: /salon|spa|beauty|haircut|grooming/i,
  restaurant: /restaurant|food|eat|dining|cafe|dine/i,
  gym: /gym|fitness|workout|exercise/i,
  pharmacy: /pharmacy|medicine|drug|chemist/i,
  greeting: /^(hi|hello|hey|good morning|good evening|good afternoon|howdy|namaste)[!?.]?$/i,
  help: /help|what can you do|how do you work|commands|options/i,
  price: /price|cost|rate|charge|how much|fee|billing/i,
  book: /book|reserve|appointment|schedule|booking/i,
  create: /create|add|new|make/i,
  coupon: /coupon|offer|discount|promo|privilege/i,
  register: /register|sign up|signup|create account|join/i
};

function detectIntent(text) {
  const lower = text.toLowerCase();
  const intents = [];
  if (INTENTS.nearby.test(lower)) intents.push("nearby");
  if (INTENTS.hotel.test(lower)) intents.push("hotel");
  if (INTENTS.hospital.test(lower)) intents.push("hospital");
  if (INTENTS.salon.test(lower)) intents.push("salon");
  if (INTENTS.restaurant.test(lower)) intents.push("restaurant");
  if (INTENTS.gym.test(lower)) intents.push("gym");
  if (INTENTS.pharmacy.test(lower)) intents.push("pharmacy");
  if (INTENTS.greeting.test(lower)) intents.push("greeting");
  if (INTENTS.help.test(lower)) intents.push("help");
  if (INTENTS.price.test(lower)) intents.push("price");
  if (INTENTS.book.test(lower)) intents.push("book");
  if (INTENTS.create.test(lower)) intents.push("create");
  if (INTENTS.coupon.test(lower)) intents.push("coupon");
  if (INTENTS.register.test(lower)) intents.push("register");
  return intents;
}

const DEPT_MAP = {
  hotel: "Hotel",
  hospital: "Hospital",
  salon: "Salon",
  restaurant: "Restaurant",
  gym: "Gym",
  pharmacy: "Pharmacy",
};

// ── ActionCard ────────────────────────────────────────────────────────
function ActionCard({ action, navigate, setOpen }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        overflow: "hidden",
        marginTop: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        width: "100%",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "8px"
      }}
    >
      <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a" }}>
        {action.title}
      </div>
      <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
        {action.description}
      </div>
      <button
        onClick={() => {
           navigate(action.url);
           if (setOpen) setOpen(false);
        }}
        style={{
          width: "100%", background: "#064e3b", color: "#fff", border: "none",
          borderRadius: "8px", padding: "7px 0", fontWeight: 700, fontSize: "0.78rem", cursor: "pointer",
        }}
      >
        {action.buttonText} →
      </button>
    </div>
  );
}

// ── PropertyCard ─────────────────────────────────────────────────────
function PropertyCard({ property, navigate, setOpen }) {
  const imgData = property.image || property.imageUrl || property.images?.[0] || property.thumbnail || property.featuredImage;
  const image = imgData
      ? getFullImageUrl(imgData)
      : "https://images.unsplash.com/photo-1540544660431-98411d9b7d99?w=400&q=60";

  const price =
    property.pricePerNight ||
    property.price ||
    property.consultationFee ||
    property.serviceCost ||
    property.basePrice ||
    null;
    
  const isPerNight = !!property.pricePerNight;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        overflow: "hidden",
        marginTop: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        width: "100%",
      }}
    >
      <img
        src={image}
        alt={property.name}
        style={{ width: "100%", height: "110px", objectFit: "cover" }}
        onError={(e) => {
          if (e.target.src !== "https://images.unsplash.com/photo-1540544660431-98411d9b7d99?w=400&q=60") {
            e.target.src = "https://images.unsplash.com/photo-1540544660431-98411d9b7d99?w=400&q=60";
          }
        }}
      />
      <div style={{ padding: "10px 12px" }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: "0.85rem",
            color: "#0f172a",
            marginBottom: "2px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {property.name || property.company || "BookMyGeo Property"}
        </div>
        <div
          style={{
            fontSize: "0.75rem",
            color: "#64748b",
            marginBottom: "6px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          📍 {property.location || property.city || property.address || "Location available"}
        </div>
        {price && (
          <div
            style={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "#064e3b",
              marginBottom: "8px",
            }}
          >
            ₹{Number(price).toLocaleString()}
            {isPerNight && (
              <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: "0.7rem" }}>
                {" "}
                / night
              </span>
            )}
          </div>
        )}
        <button
          onClick={() => {
             const dept = property.department || property.category || "Hotel";
             navigate(`/user/property-details/${dept}/${property.id || property._id}`);
             if (setOpen) setOpen(false);
          }}
          style={{
            width: "100%",
            background: "#064e3b",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "7px 0",
            fontWeight: 700,
            fontSize: "0.78rem",
            cursor: "pointer",
          }}
        >
          Book Now →
        </button>
      </div>
    </div>
  );
}

// ── Main ChatBot ─────────────────────────────────────────────────────
export default function ChatBot() {
  const navigate = useNavigate();
  const location = useLocation();
  const isVendor = location.pathname.startsWith('/host');
  
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [userCoords, setUserCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const lottieRef = useRef(null);
  const inputRef = useRef(null);
  const guidedPagesRef = useRef(new Set());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!isVendor) return;

    const path = location.pathname;
    if (guidedPagesRef.current.has(path)) return;

    let contextualMsg = null;
    if (path.includes('/create-property') || path.includes('/add-property')) {
      contextualMsg = {
        text: "You are on the Create Property page! Fill in your property name, category, and price. Upload high-quality images and click Submit at the bottom to publish your listing.",
        cards: []
      };
    } else if (path.includes('/properties')) {
      contextualMsg = {
        text: "Welcome to your Properties list! Here you can view all your active listings. Click 'Add New Property' to create a new one, or edit existing ones.",
        cards: []
      };
    } else if (path.includes('/bookings')) {
      contextualMsg = {
        text: "This is the Bookings dashboard! You can view and manage all upcoming reservations here. Click on a booking to see guest details.",
        cards: []
      };
    } else if (path.includes('/today-arrivals')) {
      contextualMsg = {
        text: "Here are today's check-ins! Make sure to verify guest IDs and booking references when they arrive.",
        cards: []
      };
    }

    if (contextualMsg) {
      guidedPagesRef.current.add(path);
      const timer = setTimeout(() => {
        setMessages(prev => [...prev, { id: Date.now(), from: "bot", ...contextualMsg }]);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isVendor]);

  useEffect(() => {
    if (messages.length === 0 || messages[0].isVendor !== isVendor) {
      setMessages([
        {
          id: Date.now(),
          from: "bot",
          isVendor: isVendor,
          text: isVendor 
            ? "👋 Hi Vendor! I'm your Host Support Assistant. Ask me about adding properties, viewing bookings, arrivals, support, or refund policies." 
            : "👋 Hi! I'm your BookMyGeo assistant.\nAsk me about hotels, hospitals, salons, restaurants, or anything nearby!",
          cards: [],
        }
      ]);
    }
  }, [isVendor, messages.length]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
      if (!userCoords && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => setUserCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
          () => {}
        );
      }
    }
  }, [open]);

  async function fetchPropertiesForCategory(dept) {
    try {
      const res = await fetch(`${API_URL}/properties/${dept}`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data.properties || data.data || [];
    } catch { return []; }
  }

  async function fetchAllProperties() {
    try {
      const res = await fetch(`${API_URL}/properties/all`);
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data.properties || data.data || [];
    } catch { return []; }
  }

  async function getUserLocation() {
    if (userCoords) return userCoords;
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lon: pos.coords.longitude };
          setUserCoords(coords);
          resolve(coords);
        },
        () => resolve(null),
        { timeout: 6000 }
      );
    });
  }

  async function generateReply(text) {
    if (detectSecurityThreat(text)) {
      return {
        text: "I can't provide or reveal confidential information such as passwords, API keys, database credentials, environment variables, internal configurations, or other security-sensitive data.",
        cards: []
      };
    }

    const normalized = normalizeText(text);
    const intents = detectIntent(normalized);
    const lowerText = text.toLowerCase();

    // Context / Page specific help (Advanced heuristic logic)
    const asksAboutPage = lowerText.includes('how do i') || lowerText.includes('how to') || lowerText.includes('what') || lowerText.includes('explain') || lowerText.includes('here') || lowerText.includes('this page');
    if (intents.includes('help') || intents.includes('create') || lowerText.includes('rate') || lowerText.includes('review') || asksAboutPage) {
      const path = location.pathname;
      const search = location.search;
      
      if (lowerText.includes('create hotel') || lowerText.includes('add property') || path.includes('/create-property') || path.includes('/add-property')) {
        return {
          text: "How to Create a Property (Hotel, Salon, etc.):\n\n1. You are already on (or should navigate to) the Add Property page.\n2. **Basic Details:** Fill in the Property Name and select the exact Category.\n3. **Location & Contact:** Enter full address and valid phone numbers (mandatory).\n4. **Pricing:** Enter your Base Price / Price per night (must be numeric).\n5. **Validation:** Ensure all asterisk (*) fields are filled out, otherwise it will show an error.\n6. **Saving:** Click Submit at the bottom. Your listing will immediately appear in your Properties dashboard.",
          cards: [{ type: 'action', title: "Add Property", description: "Create a new property listing.", buttonText: "Go to Properties", url: "/host/properties" }]
        };
      }
      
      if (path.includes('/properties') || path.includes('/branches')) {
        return {
          text: "You are on the Properties Dashboard.\n\n• **Active Listings:** Shows all your current properties.\n• **Edit:** Click on a property card to modify its details.\n• **Add New:** Use the '+ Add New Branch' button to create more listings.",
          cards: []
        };
      }
      
      if (path.includes('/bookings')) {
        return {
          text: "You are on the Bookings Dashboard.\n\n• **View:** Review all upcoming, pending, and past reservations.\n• **Action:** Click a booking to accept or reject it.\n• **Status:** Pay attention to 'Pending' vs 'Confirmed' badges.",
          cards: []
        };
      }
      
      if (path.includes('/today-arrivals')) {
        return {
          text: "You are on Today's Arrivals.\n\n• **Check-ins:** See all guests scheduled to arrive today.\n• **Verify:** Always check their booking reference ID and Photo ID upon arrival.\n• **Action:** Mark them as 'Checked In' once verified.",
          cards: []
        };
      }

      if (path.includes('/host/coupons')) {
        return {
          text: "You are on the Coupons & Offers page.\n\n• Here you can create and manage discount codes for your customers.\n• Use the 'Create New Coupon' button to set up a new promotion to boost your sales!",
          cards: []
        };
      }

      if (path.includes('/host/analytics')) {
        return {
          text: "You are on the Analytics Dashboard.\n\n• Track your total earnings, booking trends, and customer metrics here to see how your business is performing.",
          cards: []
        };
      }

      if (path.includes('/host/subscription')) {
        return {
          text: "You are on the Subscription page.\n\n• Here you can view your current host plan and upgrade to a premium tier for more features and visibility.",
          cards: []
        };
      }

      // --- USER PAGE CONTEXTS ---

      if (path.includes('/user-login')) {
        return {
          text: "You are on the User Login page.\n\n• Enter your registered email and password to log in.\n• If you don't have an account yet, click the 'Sign up' link at the bottom.",
          cards: []
        };
      }

      if (path.includes('/user-register')) {
        return {
          text: "You are on the User Registration page.\n\n• Fill out your personal details to create a new BookMyGeo account and start booking services!",
          cards: []
        };
      }

      if (path.includes('/host-login')) {
        return {
          text: "You are on the Host Login page.\n\n• Enter your vendor credentials to access your business dashboard. If you're a new partner, click 'Register as Host'.",
          cards: []
        };
      }

      if (path.includes('/host-registration')) {
        return {
          text: "You are on the Host Registration page.\n\n• Fill out your business details to partner with BookMyGeo and start listing your properties or services.",
          cards: []
        };
      }

      if (path.includes('/user/offers')) {
        return {
          text: "You are on the Special Offers page!\n\n• Here you can browse exclusive deals, loyalty rewards, and seasonal discounts curated just for you.",
          cards: []
        };
      }

      if (path.includes('/user/main-dashboard')) {
        if (search.includes('tab=cards')) {
          return {
            text: "You are on the My Membership Cards tab.\n\n• This section displays all the premium membership cards you have successfully acquired.",
            cards: []
          };
        }
        if (search.includes('tab=membership')) {
          return {
            text: "You are on the Business Membership tab.\n\n• Here you can browse available premium cards, request them, and track the approval status of your requests.",
            cards: []
          };
        }
        if (search.includes('tab=reviews')) {
          if (lowerText.includes('how') || lowerText.includes('rate') || lowerText.includes('review')) {
            return {
              text: "How to Rate & Review:\n\n1. Find the booking you want to review in your 'My Reviews' list.\n2. Click the green 'Rate Us' button on the booking card.\n3. A modal will pop up where you can select an emoji rating (Terrible to Amazing).\n4. Optionally, type in your thoughts or highlights about your stay.\n5. Click 'Publish to Vault' to save your review!",
              cards: []
            };
          }
          return {
            text: "You are on the My Reviews tab.\n\n• This shows a history of all the ratings and reviews you have posted for various services.\n• You can also edit or view chats for previous reviews.",
            cards: []
          };
        }
        if (search.includes('tab=support')) {
          return {
            text: "You are on the Support tab.\n\n• If you need help with a booking or have an issue, you can raise a ticket and contact our team here.",
            cards: []
          };
        }
        return {
          text: "You are on your User Dashboard. Use the tabs above to navigate between your memberships, reviews, and support.",
          cards: []
        };
      }
    }

    // Vendor specific intents
    if (isVendor) {
      if (intents.includes('create') || lowerText.includes('new property')) {
        return {
          text: "To add a new property, navigate to your Properties dashboard and click 'Add New Property'.",
          cards: [{ type: 'action', title: "Add Property", description: "Create a new property listing.", buttonText: "Go to Properties", url: "/host/properties" }]
        };
      }
      if (lowerText.includes('booking') || lowerText.includes('reservation') || lowerText.includes('order')) {
        return {
          text: "You can view and manage all your property bookings on the Bookings page.",
          cards: [{ type: 'action', title: "All Bookings", description: "View your reservations.", buttonText: "Go to Bookings", url: "/host/bookings" }]
        };
      }
      if (lowerText.includes('arrival') || lowerText.includes('today')) {
        return {
          text: "Check who is arriving today in the 'Today's Arrivals' dashboard.",
          cards: [{ type: 'action', title: "Today's Arrivals", description: "See today's check-ins.", buttonText: "View Arrivals", url: "/host/arrivals" }]
        };
      }
      if (lowerText.includes('refund') || lowerText.includes('support')) {
        return {
          text: "For refund policies and host support, please visit the Support page or contact our support team. Refunds for cancelled bookings depend on your set cancellation policy.",
          cards: [{ type: 'action', title: "Host Support", description: "Get help with your host account.", buttonText: "Contact Support", url: "/host/support" }]
        };
      }
      
      if (intents.includes('greeting')) {
        return {
          text: "Hello! 😊 I'm your Vendor Assistant. Ask me how to add properties, check bookings, arrivals, or support.",
          cards: []
        };
      }
      
      return {
        text: "I can help you navigate your host dashboard! Try asking about: 'How to add a property', 'Where are my bookings?', 'Today's arrivals', or 'Support details'. If you need help with the current page, just ask 'what do I do here?'.",
        cards: []
      };
    }

    // Normal User Intents
    if (intents.includes("register")) {
      return {
        text: "You can easily register for an account to unlock exclusive features! Click the button below to go to the registration page.",
        cards: [{ type: 'action', title: "Register Account", description: "Join BookMyGeo to get personalized deals and book services.", buttonText: "Sign Up Now", url: "/user-register" }],
      };
    }

    if (intents.includes("coupon")) {
      return {
        text: "You can find all available coupons and special offers in the 'Special Offers' tab on your dashboard! Click the button below to view them.",
        cards: [{ type: 'action', title: "Special Offers", description: "View all your available coupons and discounts.", buttonText: "View Offers", url: "/user/offers" }],
      };
    }

    if (intents.includes("greeting")) {
      return {
        text: "Hello! 😊 How can I help you today?\n\nYou can ask me:\n• Hotels near me\n• Show all hospitals\n• Salons nearby\n• Book a restaurant\n• How to use coupons",
        cards: [],
      };
    }

    if (intents.includes("help")) {
      return {
        text: "Here's what I can do:\n\n🏨 Hotels — list all or nearby\n🏥 Hospitals — find clinics near you\n✂️ Salons — beauty & grooming\n🍽️ Restaurants — dining options\n💊 Pharmacies — medicine stores\n\nTry: \"Hotels near me\" or \"Show salons\"",
        cards: [],
      };
    }

    const categoryIntents = ["hotel", "hospital", "salon", "restaurant", "gym", "pharmacy"];
    const matchedCategory = categoryIntents.find((c) => intents.includes(c));

    if (intents.includes("nearby") && matchedCategory) {
      const coords = await getUserLocation();
      if (!coords) {
        return {
          text: "📍 Please allow location access in your browser to find nearby places, then try again!",
          cards: [],
        };
      }
      const dept = DEPT_MAP[matchedCategory];
      const allProps = await fetchPropertiesForCategory(dept);
      if (!allProps.length) {
        return { text: `No ${dept}s found at the moment.`, cards: [] };
      }
      const sorted = allProps
        .map((p) => ({
          ...p,
          dist: p.latitude && p.longitude
            ? haversine(coords.lat, coords.lon, p.latitude, p.longitude)
            : 9999,
        }))
        .sort((a, b) => a.dist - b.dist)
        .slice(0, 4);

      return {
        text: `📍 Found ${sorted.length} nearby ${dept}s:`,
        cards: sorted,
      };
    }

    if (matchedCategory) {
      const dept = DEPT_MAP[matchedCategory];
      const props = await fetchPropertiesForCategory(dept);
      if (!props.length) {
        return { text: `No ${dept}s found right now. Please try again later!`, cards: [] };
      }
      return {
        text: `🏠 Here are some ${dept}s on BookMyGeo:`,
        cards: props.slice(0, 4),
      };
    }

    if (intents.includes("nearby")) {
      const coords = await getUserLocation();
      if (!coords) {
        return {
          text: "📍 Please allow location access to find nearby services. Which type? (Hotels, Hospitals, Salons...)",
          cards: [],
        };
      }
      const all = await fetchAllProperties();
      const sorted = all
        .map((p) => ({
          ...p,
          dist: p.latitude && p.longitude
            ? haversine(coords.lat, coords.lon, p.latitude, p.longitude)
            : 9999,
        }))
        .sort((a, b) => a.dist - b.dist)
        .slice(0, 4);
      return { text: "📍 Nearest services to you:", cards: sorted };
    }

    if (intents.includes("book")) {
      return {
        text: "To book, tell me the service type:\n• \"Book a hotel near me\"\n• \"Find salons in Chennai\"\n\nOr click Book Now on any card!",
        cards: [],
      };
    }

    // Keyword search module
    const allPropsKw = await fetchAllProperties();
    
    const keywordMatches = allPropsKw.filter(p => {
      if (!p) return false;
      const nameMatch = p.name?.toLowerCase().includes(lowerText);
      const cityMatch = p.city?.toLowerCase().includes(lowerText);
      
      let kwMatch = false;
      if (p.searchKeywords) {
        const keywords = p.searchKeywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k.length > 2);
        kwMatch = keywords.some(k => {
          const regex = new RegExp(`\\b${k}\\b`, 'i');
          return regex.test(lowerText) || lowerText.includes(k);
        });
      }
      
      return nameMatch || cityMatch || kwMatch;
    });

    if (keywordMatches.length > 0) {
      let sortedMatches = keywordMatches;
      if (userCoords) {
        sortedMatches = keywordMatches.map((p) => ({
          ...p,
          dist: p.latitude && p.longitude
            ? haversine(userCoords.lat, userCoords.lon, p.latitude, p.longitude)
            : 9999,
        })).sort((a, b) => a.dist - b.dist);
      }
      
      return {
        text: `🔍 I found some properties matching "${text}":`,
        cards: sortedMatches.slice(0, 4)
      };
    }

    return {
      text: "I'm not sure about that. Try:\n• \"Hotels near me\"\n• \"Show hospitals\"\n• \"Salons nearby\"\n• \"How to use coupons\"\n• \"How to register\"\n\nType help for all options!",
      cards: [],
    };
  }

  async function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setMessages((prev) => [...prev, { id: Date.now(), from: "user", text: trimmed, cards: [] }]);
    setInput("");
    setLoading(true);
    try {
      const reply = await generateReply(trimmed);
      setMessages((prev) => [...prev, { id: Date.now() + 1, from: "bot", ...reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, from: "bot", text: "Something went wrong. Please try again!", cards: [] },
      ]);
    } finally {
      setLoading(false);
    }
  }



  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => {
          lottieRef.current?.stop();
          lottieRef.current?.play();
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        style={{
          position: "fixed", bottom: "20px", right: "20px", zIndex: 9999,
          width: "140px", height: "140px", 
          background: "transparent",
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {open ? (
          <div style={{ background: "#064e3b", color: "#fff", width: "54px", height: "54px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}>✕</div>
        ) : (
          <Lottie lottieRef={lottieRef} animationData={liveChatbotAnimation} loop={false} autoplay={false} style={{ width: '100%', height: '100%' }} />
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            style={{
              position: "fixed", bottom: "100px", right: "24px", zIndex: 9998,
              width: "340px", maxHeight: "560px", display: "flex", flexDirection: "column",
              borderRadius: "20px", overflow: "hidden",
              boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
              background: "#fff", border: "1px solid #e2e8f0",
            }}
          >
            {/* Header */}
            <div style={{
              background: "linear-gradient(135deg, #064e3b, #0f766e)",
              padding: "14px 18px", display: "flex", alignItems: "center",
              gap: "12px", flexShrink: 0,
            }}>
              <div style={{
                width: "36px", height: "36px", borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem",
              }}>🤖</div>
              <div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
                  BookMyGeo Assistant
                </div>
                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.75rem" }}>
                  {loading ? "Typing..." : "● Online"}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div style={{
              flex: 1, overflowY: "auto", padding: "14px",
              display: "flex", flexDirection: "column", gap: "10px", background: "#f8fafc",
            }}>
              {messages.map((msg) => (
                <div key={msg.id}>
                  <div style={{ display: "flex", justifyContent: msg.from === "user" ? "flex-end" : "flex-start" }}>
                    <div style={{
                      maxWidth: "85%", padding: "10px 13px",
                      borderRadius: msg.from === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      background: msg.from === "user"
                        ? "linear-gradient(135deg, #064e3b, #0f766e)" : "#fff",
                      color: msg.from === "user" ? "#fff" : "#0f172a",
                      fontSize: "0.83rem", lineHeight: 1.5, whiteSpace: "pre-wrap",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                      border: msg.from === "bot" ? "1px solid #e2e8f0" : "none",
                    }}>
                      {msg.text}
                    </div>
                  </div>
                  {msg.cards && msg.cards.length > 0 && (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: msg.cards.length === 1 ? "1fr" : "1fr 1fr",
                      gap: "8px", marginTop: "6px",
                    }}>
                      {msg.cards.map((prop, i) => (
                        prop.type === 'action' ? (
                          <ActionCard key={i} action={prop} navigate={navigate} setOpen={setOpen} />
                        ) : (
                          <PropertyCard key={prop.id || prop._id || i} property={prop} navigate={navigate} setOpen={setOpen} />
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Typing dots */}
              {loading && (
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <div style={{
                    padding: "10px 14px", background: "#fff",
                    borderRadius: "16px 16px 16px 4px", border: "1px solid #e2e8f0",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    display: "flex", gap: "4px", alignItems: "center",
                  }}>
                    {[0, 0.2, 0.4].map((d, i) => (
                      <motion.div
                        key={i}
                        style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#0f766e" }}
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: d }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>



            {/* Input */}
            <div style={{
              padding: "10px 14px 14px", background: "#f8fafc",
              display: "flex", gap: "8px", flexShrink: 0,
              borderTop: "1px solid #e2e8f0",
            }}>
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask me anything..."
                style={{
                  flex: 1, padding: "9px 14px", borderRadius: "30px",
                  border: "1px solid #cbd5e1", fontSize: "0.83rem",
                  outline: "none", background: "#fff", color: "#0f172a",
                }}
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                style={{
                  width: "38px", height: "38px", borderRadius: "50%",
                  background: loading || !input.trim()
                    ? "#cbd5e1" : "linear-gradient(135deg, #064e3b, #0f766e)",
                  border: "none",
                  cursor: loading || !input.trim() ? "not-allowed" : "pointer",
                  color: "#fff", fontSize: "1rem",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                ➤
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
